import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useMemo, useState } from 'react';
import { InvestmentEntityAllocation } from '../../models/InvestmentEntity';
import Investor from '../../models/Investor';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import { StyledButton } from '../styles/StyledButtons';

interface RedistributeAllocationFormProps {
  investor: Investor;
  allocations: InvestmentEntityAllocation[];
  onSuccess: (updatedInvestor: Investor) => void;
  onCancel: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

interface AllocationEdit {
  id: string;
  entityId: string;
  entityName: string;
  originalAmount: number;
  newAmount: number;
  error?: string;
}

export const RedistributeAllocationForm: React.FC<RedistributeAllocationFormProps> = ({
  investor,
  allocations,
  onSuccess,
  onCancel,
  onLoadingChange,
}) => {
  const [allocationEdits, setAllocationEdits] = useState<AllocationEdit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const committedAmount = investor.committedInvestment || 0;

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Initialize allocation edits when the form opens
  useEffect(() => {
    if (allocations.length > 0) {
      const edits: AllocationEdit[] = allocations.map((allocation) => ({
        id: allocation.id,
        entityId: allocation.investmentEntity.id,
        entityName: allocation.investmentEntity.name,
        originalAmount: allocation.allocatedAmount,
        newAmount: allocation.allocatedAmount,
      }));
      setAllocationEdits(edits);
      setError(null);
    }
  }, [allocations]);

  const totalNewAmount = useMemo(() => {
    return allocationEdits.reduce((sum, edit) => sum + edit.newAmount, 0);
  }, [allocationEdits]);

  const remainingAmount = useMemo(() => {
    return committedAmount - totalNewAmount;
  }, [committedAmount, totalNewAmount]);

  const hasChanges = useMemo(() => {
    return allocationEdits.some((edit) => edit.newAmount !== edit.originalAmount);
  }, [allocationEdits]);

  const validationError = useMemo(() => {
    if (totalNewAmount > committedAmount) {
      return `Total allocation (${ExtendedLiquid.currencyMillions(totalNewAmount, false)}) cannot exceed committed investment (${ExtendedLiquid.currencyMillions(committedAmount, false)})`;
    }

    const hasNegative = allocationEdits.some((edit) => edit.newAmount < 0);
    if (hasNegative) {
      return 'Allocation amounts cannot be negative';
    }

    const hasZero = allocationEdits.some((edit) => edit.newAmount === 0);
    if (hasZero) {
      return 'Allocation amounts must be greater than zero';
    }

    return null;
  }, [allocationEdits, totalNewAmount, committedAmount]);

  const handleAmountChange = (editIndex: number, newAmount: number) => {
    setAllocationEdits((prev) =>
      prev.map((edit, index) =>
        index === editIndex ? { ...edit, newAmount, error: undefined } : edit,
      ),
    );
  };

  const handleDistributeExcess = (targetIndex: number) => {
    if (remainingAmount <= 0) return;

    setAllocationEdits((prev) =>
      prev.map((edit, index) =>
        index === targetIndex
          ? {
              ...edit,
              newAmount: edit.newAmount + remainingAmount,
              error: undefined,
            }
          : edit,
      ),
    );
  };

  const handleDistributeDeficit = (targetIndex: number) => {
    if (remainingAmount >= 0) return;

    const deficitToDistribute = Math.abs(remainingAmount);
    setAllocationEdits((prev) =>
      prev.map((edit, index) =>
        index === targetIndex
          ? {
              ...edit,
              newAmount: Math.max(0, edit.newAmount - deficitToDistribute),
              error: undefined,
            }
          : edit,
      ),
    );
  };

  const handleReset = () => {
    setAllocationEdits((prev) =>
      prev.map((edit) => ({
        ...edit,
        newAmount: edit.originalAmount,
        error: undefined,
      })),
    );
  };

  const handleSave = async () => {
    if (validationError) return;

    try {
      setLoading(true);
      setError(null);

      const backendService = new BackendService();
      const redistributionData = allocationEdits.map((edit) => ({
        investmentEntityId: edit.entityId,
        amount: edit.newAmount,
      }));

      await backendService.updateInvestorAllocations(investor.id, redistributionData);

      // Refresh investor data
      const updatedInvestor = await backendService.getInvestor(investor.id);
      onSuccess(updatedInvestor);
    } catch (error) {
      console.error('Failed to redistribute allocations:', error);
      setError(error instanceof Error ? error.message : 'Failed to redistribute allocations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Redistribute Investment Entity Allocations
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Investor:</strong> {investor.name}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Total Committed:</strong>{' '}
          {ExtendedLiquid.currencyMillions(committedAmount, false)}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontWeight: 'bold',
            color:
              remainingAmount < 0
                ? 'error.main'
                : remainingAmount > 0
                  ? 'warning.main'
                  : 'success.main',
          }}
        >
          <strong>Remaining After Redistribution:</strong>{' '}
          {ExtendedLiquid.currencyMillions(remainingAmount, false)}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {validationError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {validationError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <StyledButton
          variant="outlined"
          size="small"
          onClick={handleReset}
          disabled={loading || !hasChanges}
        >
          Reset to Original
        </StyledButton>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Entity</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Type</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Original</strong>
              </TableCell>
              <TableCell align="right">
                <strong>New Amount</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Change</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allocationEdits.map((edit, index) => {
              const change = edit.newAmount - edit.originalAmount;
              const allocation = allocations.find((a) => a.id === edit.id);

              return (
                <TableRow key={edit.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {edit.entityName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={allocation?.investmentEntity.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {ExtendedLiquid.currencyMillions(edit.originalAmount, false)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={edit.newAmount}
                      onChange={(e) => handleAmountChange(index, parseFloat(e.target.value) || 0)}
                      disabled={loading}
                      error={Boolean(edit.error)}
                      helperText={edit.error}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      sx={{ minWidth: 120 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color={
                          change === 0
                            ? 'text.secondary'
                            : change > 0
                              ? 'success.main'
                              : 'error.main'
                        }
                        fontWeight={change !== 0 ? 'medium' : 'normal'}
                      >
                        {change === 0
                          ? 'No change'
                          : (change > 0 ? '+' : '') +
                            ExtendedLiquid.currencyMillions(change, false)}
                      </Typography>

                      {/* Convenience buttons for excess/deficit distribution */}
                      {remainingAmount > 0 && index !== 0 && (
                        <StyledButton
                          size="small"
                          variant="outlined"
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            height: 'auto',
                          }}
                          onClick={() => handleDistributeExcess(index)}
                          disabled={loading}
                          title={`Add ${ExtendedLiquid.currencyMillions(remainingAmount, false)} excess`}
                        >
                          +{ExtendedLiquid.currencyMillions(remainingAmount, false)}
                        </StyledButton>
                      )}

                      {remainingAmount < 0 && index !== 0 && (
                        <StyledButton
                          size="small"
                          variant="outlined"
                          color="error"
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            height: 'auto',
                          }}
                          onClick={() => handleDistributeDeficit(index)}
                          disabled={loading}
                          title={`Subtract ${ExtendedLiquid.currencyMillions(Math.abs(remainingAmount), false)} deficit`}
                        >
                          -{ExtendedLiquid.currencyMillions(Math.abs(remainingAmount), false)}
                        </StyledButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
        <StyledButton variant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </StyledButton>
        <StyledButton
          variant="contained"
          onClick={handleSave}
          disabled={loading || !!validationError || !hasChanges}
        >
          {loading ? 'Redistributing...' : 'Save Redistribution'}
        </StyledButton>
      </Box>
    </Box>
  );
};
